import React, { useState, useRef } from 'react';
import { fmtHours } from '../../utils/formatters.js';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDayLabel(iso) {
  const d = new Date(iso + 'T12:00:00Z');
  return `${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

function isWeekend(iso) {
  const d = new Date(iso + 'T12:00:00Z');
  const dow = d.getUTCDay();
  return dow === 0 || dow === 6;
}

// Days is an array of { date, hours, future }. Renders one bar per day for the
// month, with weekend bars and future days at lower opacity. Hovering a bar
// shows a small dark tooltip with "<Month> <Day> · <hours>h".
export default function DailyHoursChart({ days = [], height = 96, gap = 3 }) {
  if (!days.length) return null;

  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);

  const max = Math.max(8, ...days.map(d => d.hours || 0));
  const width = 800;
  const barW = (width - gap * (days.length - 1)) / days.length;

  function onEnter(day, e) {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover({ x: e.clientX - rect.left, y: e.clientY - rect.top, day });
  }

  function onMove(e) {
    if (!hover) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover((h) => h ? { ...h, x: e.clientX - rect.left, y: e.clientY - rect.top } : null);
  }

  // ttMaxLeft is computed inside the tooltip block (below) so `wrapRef.current`
  // is non-null and reflects the live CSS pixel width of the container.

  return (
    <div
      ref={wrapRef}
      style={{ position: 'relative' }}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
    >
      <svg viewBox={`0 0 ${width} ${height + 16}`} width="100%" height={height + 16} aria-hidden="true">
        <line x1="0" y1={height - 0.5} x2={width} y2={height - 0.5} stroke="#F0EFE9" strokeWidth="1" />
        {days.map((d, i) => {
          const x = i * (barW + gap);
          const h = Math.max(0, (d.hours / max) * (height - 4));
          const y = height - h;
          const weekend = isWeekend(d.date);
          const opacity = d.future ? 0.18 : weekend ? 0.4 : 0.92;
          return (
            <g key={d.date}>
              <rect
                x={x.toFixed(1)} y={y.toFixed(1)}
                width={barW.toFixed(1)} height={h.toFixed(1)}
                fill="#3A3A38" opacity={opacity} rx="1.5"
              />
              <rect
                x={x.toFixed(1)} y="0"
                width={(barW + gap).toFixed(1)} height={height}
                fill="transparent"
                onMouseEnter={(e) => onEnter(d, e)}
                style={{ cursor: 'default' }}
              />
            </g>
          );
        })}
        {days.map((d, i) => {
          const dom = parseInt(d.date.slice(-2), 10);
          if (dom % 5 !== 0 && dom !== 1) return null;
          const x = i * (barW + gap) + barW / 2;
          return (
            <text
              key={`t-${d.date}`}
              x={x.toFixed(1)} y={height + 12}
              textAnchor="middle"
              style={{ fontSize: 9, fill: '#9A9A95' }}
            >{dom}</text>
          );
        })}
      </svg>

      {hover && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(hover.x + 12, (wrapRef.current?.clientWidth || 800) - 160 - 8),
            // -32 accounts for the single-line tooltip's ~21px height plus
            // the 11px gap above the cursor.
            top:  hover.y - 32,
            pointerEvents: 'none',
            background: '#1A1A1A', color: '#fff',
            fontSize: 11, lineHeight: 1.35,
            padding: '6px 8px', borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
            whiteSpace: 'nowrap',
            maxWidth: 160
          }}
        >
          {fmtDayLabel(hover.day.date)} · {fmtHours(hover.day.hours)}h
        </div>
      )}
    </div>
  );
}
